/**
 * The handful of api.soundcloud.com calls Fadila makes, on top of a token
 * *getter* rather than a token - every request asks
 * soundCloudAuth.getFreshAccessToken() first, which is a no-op until expiry
 * is close and a refresh when it is. Same arrangement as spotifyApi.ts.
 *
 * Notes that are SoundCloud's, not ours (docs/SOUNDCLOUD.md section 1):
 *
 * - Resources are addressed by URN (`soundcloud:tracks:123`), never numeric
 *   id. URNs go into paths as they are; the API takes the colons.
 * - The header is `Authorization: OAuth <token>`, not `Bearer`.
 * - Paging is `linked_partitioning=true` → `{ collection, next_href }`, with
 *   no total. The QueryIterators below leave `totalResults` undefined and
 *   the lists size themselves by what has been loaded, as they do for
 *   YouTube, whose totals are wrong for a different reason.
 * - `limit` maxes at 200.
 */

import QueryIterator from '../../models/queryIterator';
import SoundCloudApiTrack, { SoundCloudAccess } from '../../models/soundcloud/soundCloudApiTrack';
import SoundCloudPage from '../../models/soundcloud/soundCloudPage';
import SoundCloudPlaylist from '../../models/soundcloud/soundCloudPlaylist';
import SoundCloudStreams from '../../models/soundcloud/soundCloudStreams';
import SoundCloudUser from '../../models/soundcloud/soundCloudUser';

export const apiUrl = 'https://api.soundcloud.com';

/** The most any list endpoint returns per page. */
export const pageLimitMax = 200;

/** Resolves to the current access token, or undefined when signed out. */
export type TokenSource = () => Promise<string | undefined>;

/**
 * A non-2xx from the API, with the status the service's error handler
 * switches on. `body` is whatever JSON came back, for the curious.
 */
export class SoundCloudApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: any,
  ) {
    super(message);
    this.name = 'SoundCloudApiError';
  }
}

/**
 * The API's error bodies vary: `{ code, message, errors: [{ error_message }] }`
 * on most, `{ error, error_description }` from OAuth-flavoured ones, and
 * `{ remaining, reset }` beside a 429 on streams. Take the first thing that
 * reads as a sentence.
 */
function describeError(status: number, body: any) {
  if (body) {
    if (typeof body.message === 'string') {
      return body.message;
    }

    if (Array.isArray(body.errors) && body.errors[0] && body.errors[0].error_message) {
      return body.errors[0].error_message;
    }

    if (typeof body.error_description === 'string') {
      return body.error_description;
    }

    if (typeof body.error === 'string') {
      return body.error;
    }
  }

  switch (status) {
    case 401:
      return 'SoundCloud sign-in has expired.';
    case 403:
      return 'SoundCloud does not allow this.';
    case 404:
      return 'SoundCloud does not have this any more.';
    case 429:
      return 'SoundCloud is rate limiting Fadila right now.';
    default:
      return `SoundCloud returned ${status}.`;
  }
}

async function soundCloudRequest(
  url: string,
  getToken: TokenSource,
  method = 'GET',
  skipApiUrl = false,
  body?: any,
): Promise<any> {
  const accessToken = await getToken();

  if (accessToken === undefined) {
    throw new SoundCloudApiError('Not signed in to SoundCloud.', 401);
  }

  const headers: { [key: string]: string } = {
    Authorization: `OAuth ${accessToken}`,
    Accept: 'application/json; charset=utf-8',
  };

  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(skipApiUrl ? url : apiUrl + url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 204) {
    return;
  }

  const text = await res.text();
  let json: any;

  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }

  if (!res.ok) {
    throw new SoundCloudApiError(describeError(res.status, json), res.status, json);
  }

  return json;
}

function pageLimit(limit?: number) {
  return Math.min(pageLimitMax, Math.max(1, limit || pageLimitMax));
}

/**
 * Wraps one page as a QueryIterator whose `next` fetches `next_href`. Some
 * endpoints answer a bare array when not asked to partition; that is a
 * single page.
 */
function pageToIterator<T>(page: SoundCloudPage<T> | T[], getToken: TokenSource): QueryIterator<T> {
  const collection = Array.isArray(page) ? page : (page && page.collection) || [];
  const nextHref = Array.isArray(page) ? undefined : page && page.next_href;

  return {
    results: collection,
    totalResults: undefined,
    next: nextHref
      ? async function nextPage() {
          return pageToIterator<T>(
            await soundCloudRequest(nextHref, getToken, 'GET', true),
            getToken,
          );
        }
      : undefined,
  };
}

export async function getUserProfile(getToken: TokenSource): Promise<SoundCloudUser> {
  return await soundCloudRequest('/me', getToken);
}

/** The signed-in user's likes, newest first. */
export async function getLikedTracks(
  getToken: TokenSource,
  limit?: number,
): Promise<QueryIterator<SoundCloudApiTrack>> {
  return pageToIterator<SoundCloudApiTrack>(
    await soundCloudRequest(
      `/me/likes/tracks?limit=${pageLimit(limit)}&linked_partitioning=true`,
      getToken,
    ),
    getToken,
  );
}

/** The signed-in user's own playlists, without their tracks. */
export async function getUserPlaylists(
  getToken: TokenSource,
  limit?: number,
): Promise<QueryIterator<SoundCloudPlaylist>> {
  return pageToIterator<SoundCloudPlaylist>(
    await soundCloudRequest(
      `/me/playlists?show_tracks=false&limit=${pageLimit(limit)}&linked_partitioning=true`,
      getToken,
    ),
    getToken,
  );
}

export async function getPlaylistTracks(
  getToken: TokenSource,
  playlistUrn: string,
  limit?: number,
): Promise<QueryIterator<SoundCloudApiTrack>> {
  return pageToIterator<SoundCloudApiTrack>(
    await soundCloudRequest(
      `/playlists/${playlistUrn}/tracks?limit=${pageLimit(limit)}&linked_partitioning=true`,
      getToken,
    ),
    getToken,
  );
}

export async function getPlaylist(
  getToken: TokenSource,
  playlistUrn: string,
): Promise<SoundCloudPlaylist> {
  return await soundCloudRequest(`/playlists/${playlistUrn}?show_tracks=false`, getToken);
}

/**
 * Every track of a playlist, following the pages to the end.
 *
 * The sync needs all of them or none: `updatePlaylistTracks` replaces the
 * whole list, so a half-read playlist would write back a truncated one.
 */
export async function getAllPlaylistTracks(
  getToken: TokenSource,
  playlistUrn: string,
): Promise<SoundCloudApiTrack[]> {
  const all: SoundCloudApiTrack[] = [];
  let page = await getPlaylistTracks(getToken, playlistUrn);

  for (;;) {
    all.push(...page.results);

    if (!page.next) {
      return all;
    }

    page = await page.next();
  }
}

/**
 * Replaces a playlist's track list.
 *
 * SoundCloud has no per-item add or remove: `PUT /playlists/{urn}` takes the
 * entire list and whatever is not in it is gone. So this is the one call
 * that can destroy something, and the caller must have read every page
 * first - see `getAllPlaylistTracks` and the check in the sync provider.
 *
 * Only `tracks` is sent, so title, description and sharing are left as they
 * are. Order is the caller's: the sync keeps the remote's own order and
 * appends what is new, since ordering is not something Fadila syncs.
 */
export async function updatePlaylistTracks(
  getToken: TokenSource,
  playlistUrn: string,
  trackUrns: string[],
): Promise<SoundCloudPlaylist> {
  return await soundCloudRequest(`/playlists/${playlistUrn}`, getToken, 'PUT', false, {
    playlist: {
      tracks: trackUrns.map((urn) => ({ urn })),
    },
  });
}

/**
 * Track search. `access=playable,preview` keeps blocked tracks out; a search
 * result that cannot be played is not a result.
 */
export async function searchTracks(
  getToken: TokenSource,
  q: string,
  limit?: number,
): Promise<QueryIterator<SoundCloudApiTrack>> {
  return pageToIterator<SoundCloudApiTrack>(
    await soundCloudRequest(
      `/tracks?q=${encodeURIComponent(q)}&access=playable,preview` +
        `&limit=${pageLimit(limit)}&linked_partitioning=true`,
      getToken,
    ),
    getToken,
  );
}

export async function searchPlaylists(
  getToken: TokenSource,
  q: string,
  limit?: number,
): Promise<QueryIterator<SoundCloudPlaylist>> {
  return pageToIterator<SoundCloudPlaylist>(
    await soundCloudRequest(
      `/playlists?q=${encodeURIComponent(q)}&show_tracks=false` +
        `&limit=${pageLimit(limit)}&linked_partitioning=true`,
      getToken,
    ),
    getToken,
  );
}

export type SoundCloudResource = SoundCloudApiTrack | SoundCloudPlaylist | SoundCloudUser;

/** True when the string is a soundcloud.com page, which `resolve` can turn into a resource. */
export function isSoundCloudUrl(str: string) {
  return /^https?:\/\/(?:www\.|m\.|on\.)?soundcloud\.com\/\S+/i.test(str.trim());
}

/**
 * A pasted soundcloud.com URL → the track, playlist or user it points at.
 * The API answers with a redirect to the resource, which fetch follows.
 */
export async function resolve(getToken: TokenSource, url: string): Promise<SoundCloudResource> {
  return await soundCloudRequest(`/resolve?url=${encodeURIComponent(url.trim())}`, getToken);
}

export async function getStreams(
  getToken: TokenSource,
  trackUrn: string,
): Promise<SoundCloudStreams> {
  return await soundCloudRequest(`/tracks/${trackUrn}/streams`, getToken);
}

export interface SoundCloudStream {
  /** A signed CDN URL that needs no header. Session-scoped: never store it. */
  url: string;
  /** `hls` is a manifest (fMP4 AAC or MP3 segments); `mp3` a plain file. */
  kind: 'hls' | 'mp3';
  /** What this stream actually is - `preview` when the full track was not offered. */
  access: SoundCloudAccess;
}

/**
 * Turns a track into something an `<audio>` element (or hls.js) can play.
 *
 * `/tracks/{urn}/streams` gives URLs on api.soundcloud.com that 401 without
 * the OAuth header, which `<audio>` cannot send. With the header each one is
 * a 302 to a signed CDN manifest that needs no header, so: fetch it here,
 * let fetch follow the redirect (the browser drops `Authorization` on the
 * cross-origin hop, which is what the CDN wants) and hand back
 * `response.url`. The body is the manifest, or for a preview the MP3
 * itself - neither is wanted, so the download is cancelled.
 *
 * Prefers AAC, falls back to MP3-in-HLS, then to the preview - and says
 * which, so the player can tell the user they are hearing 30 seconds.
 */
export async function resolveStream(
  getToken: TokenSource,
  trackUrn: string,
  access?: SoundCloudAccess,
): Promise<SoundCloudStream> {
  const streams = await getStreams(getToken, trackUrn);

  const full = streams.hls_aac_160_url || streams.hls_mp3_128_url;
  const preview = streams.preview_mp3_128_url;

  let candidate: string | undefined;
  let kind: SoundCloudStream['kind'];
  let resolvedAccess: SoundCloudAccess;

  if (access !== 'preview' && full) {
    candidate = full;
    kind = 'hls';
    resolvedAccess = 'playable';
  } else if (preview) {
    candidate = preview;
    kind = 'mp3';
    resolvedAccess = 'preview';
  } else {
    throw new SoundCloudApiError('SoundCloud offers no stream for this track.', 403, streams);
  }

  const accessToken = await getToken();

  if (accessToken === undefined) {
    throw new SoundCloudApiError('Not signed in to SoundCloud.', 401);
  }

  const res = await fetch(candidate, {
    headers: { Authorization: `OAuth ${accessToken}` },
    redirect: 'follow',
  });

  if (!res.ok) {
    let body: any;

    try {
      body = await res.json();
    } catch {
      body = undefined;
    }

    throw new SoundCloudApiError(describeError(res.status, body), res.status, body);
  }

  if (res.body) {
    res.body.cancel().catch(function ignore() {});
  }

  return {
    url: res.url,
    kind,
    access: resolvedAccess,
  };
}

/**
 * Revokes the token. The terms want personal data gone within seven days of
 * a disconnect; we hold none, but revoking is the polite half of logging out.
 */
export async function signOut(getToken: TokenSource) {
  return await soundCloudRequest('/sign-out', getToken, 'POST');
}

/**
 *
 * @param getToken Resolves to a fresh SoundCloud access token
 * @param wrapper F.x. exception handling
 */
export default function configureSoundCloudApi(
  getToken: TokenSource,
  wrapper = <T>(x: () => Promise<T>) => x(),
) {
  return {
    getUserProfile: () => wrapper(() => getUserProfile(getToken)),
    getLikedTracks: (limit?: number) => wrapper(() => getLikedTracks(getToken, limit)),
    getUserPlaylists: (limit?: number) => wrapper(() => getUserPlaylists(getToken, limit)),
    getPlaylist: (playlistUrn: string) => wrapper(() => getPlaylist(getToken, playlistUrn)),
    getPlaylistTracks: (playlistUrn: string, limit?: number) =>
      wrapper(() => getPlaylistTracks(getToken, playlistUrn, limit)),
    getAllPlaylistTracks: (playlistUrn: string) =>
      wrapper(() => getAllPlaylistTracks(getToken, playlistUrn)),
    updatePlaylistTracks: (playlistUrn: string, trackUrns: string[]) =>
      wrapper(() => updatePlaylistTracks(getToken, playlistUrn, trackUrns)),

    searchTracks: (q: string, limit?: number) => wrapper(() => searchTracks(getToken, q, limit)),
    searchPlaylists: (q: string, limit?: number) =>
      wrapper(() => searchPlaylists(getToken, q, limit)),
    resolve: (url: string) => wrapper(() => resolve(getToken, url)),

    getStreams: (trackUrn: string) => wrapper(() => getStreams(getToken, trackUrn)),
    resolveStream: (trackUrn: string, access?: SoundCloudAccess) =>
      wrapper(() => resolveStream(getToken, trackUrn, access)),
  };
}
